import React from 'react'
import useSWRMutation from 'swr/mutation'

import { submitFeedback } from '../../api'
import { useAlerts } from '../../context/alerts'
import InvoiceOverlayModal from '../modals'
import Button, { ButtonVariant } from '../ui/button'
import SelectBox from '../ui/selectBox'
import TextArea from '../ui/input/textArea'
import { snoozeFeedbackBanner } from '../../lib/feedbackStorage'

const FEEDBACK_TYPE_OPTIONS = [
  { label: 'Suggestion', value: 'suggestion' },
  { label: 'Feature request', value: 'feature_request' },
  { label: 'Something isn\'t working', value: 'error_reporting' },
]

const MAX_MESSAGE_LENGTH = 1000

export type FeedbackModalProps = {
  open: boolean
  onClose: () => void
  onSubmitted: () => void
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose, onSubmitted }) => {
  const { addAlert } = useAlerts()
  const { trigger: triggerSubmit, isMutating } = useSWRMutation('/api/user/feedback', submitFeedback)
  const [type, setType] = React.useState('')
  const [message, setMessage] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    setType('')
    setMessage('')
  }, [open])

  const handleClose = () => {
    if (isMutating) return
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedMessage = message.trim()
    if (!trimmedMessage) {
      addAlert({ type: 'error', message: 'Please enter a message.' })
      return
    }

    try {
      await triggerSubmit({
        body: {
          ...(type ? { type } : {}),
          message: trimmedMessage,
        },
      })
      snoozeFeedbackBanner()
      addAlert({ type: 'info', message: 'Thanks for your feedback!' })
      onSubmitted()
      onClose()
    } catch {
      addAlert({ type: 'error', message: 'Could not send feedback. Please try again.' })
    }
  }

  if (!open) return null

  return (
    <InvoiceOverlayModal
      open={open}
      onClose={handleClose}
      title='What would make TinyInvoice.app even better for you?'
      description='Share a suggestion, request a feature, or report an issue.'
    >
      <form onSubmit={handleSubmit}>
        <div className='mb-3'>
          <SelectBox
            id='feedback-type'
            name='type'
            label='Type'
            placeHolder='Select (optional)'
            options={FEEDBACK_TYPE_OPTIONS}
            value={type || undefined}
            onChange={(e) => setType(e.target.value ?? '')}
            isClearable
          />
        </div>
        <div className='mb-3'>
          <TextArea
            id='feedback-message'
            label='Message'
            required
            rows={4}
            maxLength={MAX_MESSAGE_LENGTH}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className='form-text text-end'>
            {message.length}/{MAX_MESSAGE_LENGTH}
          </div>
        </div>
        <div className='d-flex justify-content-end gap-2'>
          <Button type='button' variant={ButtonVariant.LIGHT} onClick={handleClose} disabled={isMutating}>
            Cancel
          </Button>
          <Button type='submit' loading={isMutating} loadingText='Sending' visibleLoadingText>
            Submit
          </Button>
        </div>
      </form>
    </InvoiceOverlayModal>
  )
}

export default FeedbackModal
